import type { CaseLayout, InboxFieldResponse } from "@/generated";

export const GRID_COLS = 12;
export const MIN_CELL_W = 3;

export type EditableCell = {
  field_id: string;
  x: number;
  y: number;
  w: number;
};
export type EditableSection = { title: string | null; cells: EditableCell[] };
export type EditableLayout = { version: 1; sections: EditableSection[] };

/** Grid rows a field occupies — taller widgets span more rows. Used for
 * reading order in the model and the case view's CSS grid-row span. */
export const fieldGridHeight = (field: InboxFieldResponse): number =>
  field.type === "table" ? 3 : field.type === "textarea" ? 2 : 1;

export const fromApi = (
  layout: CaseLayout | null | undefined
): EditableLayout | null => {
  if (!layout || !Array.isArray(layout.sections)) return null;
  const sections: EditableSection[] = [];
  for (const section of layout.sections) {
    if (!Array.isArray(section.cells)) return null;
    sections.push({
      title: section.title ?? null,
      cells: section.cells.map((c) => ({
        field_id: c.field_id,
        x: c.x,
        y: c.y,
        w: c.w,
      })),
    });
  }
  return { version: 1, sections };
};

export const toApi = (layout: EditableLayout): CaseLayout => layout;

export const layoutFieldIds = (layout: EditableLayout): Set<string> => {
  const ids = new Set<string>();
  for (const section of layout.sections)
    for (const cell of section.cells) ids.add(cell.field_id);
  return ids;
};

export const readingOrder = <T extends { x: number; y: number }>(
  cells: T[]
): T[] => [...cells].sort((a, b) => a.y - b.y || a.x - b.x);

const clone = (layout: EditableLayout): EditableLayout =>
  structuredClone(layout);

const sectionBottom = (
  section: EditableSection,
  heightOf: (fieldId: string) => number
): number =>
  section.cells.reduce(
    (max, c) => Math.max(max, c.y + heightOf(c.field_id)),
    0
  );

export const heightLookup = (
  fields: InboxFieldResponse[]
): ((fieldId: string) => number) => {
  const byId = new Map(fields.map((f) => [f.id, fieldGridHeight(f)]));
  return (fieldId) => byId.get(fieldId) ?? 1;
};

const visibleFields = (fields: InboxFieldResponse[]): InboxFieldResponse[] =>
  fields.filter((f) => f.case_view !== "hidden");

/** Seed a layout that mirrors today's auto-placement: groups become titled
 * sections, tables are hoisted out of cards, scalar fields pair up 50/50,
 * textareas and trailing singletons take a full row. */
export const computeAutoLayout = (
  fields: InboxFieldResponse[]
): EditableLayout => {
  const ordered = [...visibleFields(fields)].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
  const sections: EditableSection[] = [];
  const cursors = new Map<EditableSection, { x: number; y: number }>();
  const bySectionTitle = new Map<string | null, EditableSection>();
  const sectionFor = (title: string | null): EditableSection => {
    let section = bySectionTitle.get(title);
    if (!section) {
      section = { title, cells: [] };
      bySectionTitle.set(title, section);
      sections.push(section);
      cursors.set(section, { x: 0, y: 0 });
    }
    return section;
  };
  for (const field of ordered) {
    // Tables render outside group cards in the auto view; mirror that here.
    const title = field.type === "table" ? null : (field.group ?? null);
    const section = sectionFor(title);
    const cursor = cursors.get(section)!;
    const h = fieldGridHeight(field);
    const w = h > 1 ? GRID_COLS : 6;
    if (cursor.x + w > GRID_COLS) {
      cursor.x = 0;
      cursor.y += 1;
    }
    section.cells.push({ field_id: field.id, x: cursor.x, y: cursor.y, w });
    if (cursor.x + w >= GRID_COLS) {
      cursor.x = 0;
      cursor.y += h;
    } else {
      cursor.x += w;
    }
  }
  // Widen dangling half-width cells that ended up alone on their row.
  for (const section of sections) {
    for (const cell of section.cells) {
      const alone = !section.cells.some(
        (other) => other !== cell && other.y === cell.y
      );
      if (alone && cell.w < GRID_COLS && cell.x === 0) cell.w = GRID_COLS;
    }
  }
  return { version: 1, sections };
};

export type ReconciledCell = {
  field: InboxFieldResponse;
  x: number;
  y: number;
  w: number;
  h: number;
};
export type ReconciledSection = {
  title: string | null;
  cells: ReconciledCell[];
};

/** Resolve a stored layout against the current fields for rendering: cells
 * of deleted/hidden fields are dropped, and visible fields missing from the
 * layout are appended as full-width rows (to the section matching their
 * group when one exists). */
export const reconcileLayout = (
  layout: EditableLayout,
  fields: InboxFieldResponse[],
  // The case view hides field-less sections; the designer keeps them.
  keepEmptySections = false
): ReconciledSection[] => {
  const byId = new Map(visibleFields(fields).map((f) => [f.id, f]));
  const sections: ReconciledSection[] = layout.sections.map((section) => ({
    title: section.title,
    cells: readingOrder(section.cells).flatMap((cell) => {
      const field = byId.get(cell.field_id);
      return field
        ? [
            {
              field,
              x: cell.x,
              y: cell.y,
              w: cell.w,
              h: fieldGridHeight(field),
            },
          ]
        : [];
    }),
  }));
  const placed = layoutFieldIds(layout);
  const unplaced = [...byId.values()]
    .filter((f) => !placed.has(f.id))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const field of unplaced) {
    const title = field.group ?? null;
    let section = sections.find((s) => (s.title ?? null) === title);
    if (!section) {
      section = { title, cells: [] };
      sections.push(section);
    }
    const bottom = section.cells.reduce(
      (max, c) => Math.max(max, c.y + c.h),
      0
    );
    section.cells.push({
      field,
      x: 0,
      y: bottom,
      w: GRID_COLS,
      h: fieldGridHeight(field),
    });
  }
  return keepEmptySections
    ? sections
    : sections.filter((s) => s.cells.length > 0);
};

export const moveFieldToSection = (
  layout: EditableLayout,
  fieldId: string,
  targetIndex: number,
  heightOf: (fieldId: string) => number
): EditableLayout => {
  const next = clone(layout);
  const target = next.sections[targetIndex];
  if (!target) return layout;
  let moved: EditableCell | null = null;
  for (const section of next.sections) {
    const index = section.cells.findIndex((c) => c.field_id === fieldId);
    if (index !== -1) {
      [moved] = section.cells.splice(index, 1);
      break;
    }
  }
  if (!moved || target.cells.some((c) => c.field_id === fieldId)) {
    return layout;
  }
  target.cells.push({
    ...moved,
    x: 0,
    y: sectionBottom(target, heightOf),
    w: GRID_COLS,
  });
  return next;
};

/** Project the layout onto a subset of fields (e.g. the properties a task
 * references): cells for absent fields drop, rows re-pack to stay full, and
 * emptied sections disappear. */
export const projectLayout = (
  layout: EditableLayout,
  presentIds: Set<string>,
  heightOf: (fieldId: string) => number
): EditableLayout => ({
  version: 1,
  sections: layout.sections
    .map((section) => ({
      title: section.title,
      cells: normalizeFullRows(
        section.cells.filter((c) => presentIds.has(c.field_id)),
        heightOf
      ),
    }))
    .filter((s) => s.cells.length > 0),
});

/** Distribute widths proportionally so they sum exactly to `total`,
 * respecting the minimum cell width where possible. */
export const distributeRowWidths = (
  widths: number[],
  total: number = GRID_COLS
): number[] => {
  const n = widths.length;
  if (n === 0) return [];
  if (n === 1) return [total];
  // A cell already below the general minimum keeps its narrower width as
  // its own floor, so squeezed compact widgets survive re-normalization.
  const minAt = (i: number) =>
    Math.max(
      1,
      Math.min(MIN_CELL_W, widths[i] || MIN_CELL_W, Math.floor(total / n))
    );
  const sum = widths.reduce((a, b) => a + b, 0) || n;
  const result = widths.map((w, i) =>
    Math.max(minAt(i), Math.round(((w || 1) / sum) * total))
  );
  let diff = total - result.reduce((a, b) => a + b, 0);
  while (diff > 0) {
    result[result.indexOf(Math.max(...result))] += 1;
    diff -= 1;
  }
  while (diff < 0) {
    const shrinkable = result
      .map((w, i) => ({ w, i }))
      .filter(({ w, i }) => w > minAt(i))
      .sort((a, b) => b.w - a.w);
    if (shrinkable.length === 0) break;
    result[shrinkable[0].i] -= 1;
    diff += 1;
  }
  return result;
};

/** Group cells into rows by their top y, in reading order. */
export const groupRows = (cells: EditableCell[]): EditableCell[][] => {
  const ordered = readingOrder(cells);
  const rows: EditableCell[][] = [];
  let currentY: number | null = null;
  for (const cell of ordered) {
    if (currentY === null || cell.y !== currentY) {
      rows.push([cell]);
      currentY = cell.y;
    } else {
      rows[rows.length - 1].push(cell);
    }
  }
  return rows;
};

// Tall fields (textarea, table) own their row; a type change can leave one
// sharing a row, so normalization hoists it out.
const splitTallRows = (
  rows: EditableCell[][],
  heightOf: (fieldId: string) => number
): EditableCell[][] => {
  const split: EditableCell[][] = [];
  for (const row of rows) {
    const tall = row.filter((c) => heightOf(c.field_id) > 1);
    if (row.length <= 1 || tall.length === 0) {
      split.push(row);
      continue;
    }
    const short = row.filter((c) => heightOf(c.field_id) === 1);
    if (short.length > 0) split.push(short);
    for (const cell of tall) split.push([cell]);
  }
  return split;
};

const flattenRows = (
  rows: EditableCell[][],
  heightOf: (fieldId: string) => number
): EditableCell[] => {
  const result: EditableCell[] = [];
  let y = 0;
  for (const row of splitTallRows(rows, heightOf)) {
    if (row.length === 0) continue;
    const widths = distributeRowWidths(row.map((c) => c.w));
    let x = 0;
    row.forEach((cell, i) => {
      result.push({ ...cell, x, y, w: widths[i] });
      x += widths[i];
    });
    y += Math.max(...row.map((c) => heightOf(c.field_id)));
  }
  return result;
};

/** Snap a section's cells to full rows: dense row packing (no vertical
 * gaps), each row's widths stretched/redistributed to fill the grid. Cells
 * sharing a top y form a row; full-width tall cells sit alone. */
export const normalizeFullRows = (
  cells: EditableCell[],
  heightOf: (fieldId: string) => number
): EditableCell[] => flattenRows(groupRows(cells), heightOf);

/** Where a dragged field lands: joining an existing row (dividing its
 * space) or forming a new row before another row / at the end. */
export type FieldDropTarget =
  | { kind: "merge"; rowY: number; index: number }
  | { kind: "before-row"; rowY: number }
  | { kind: "end" }
  // Loose (ungrouped) full-width row at a layout position between sections;
  // `index` is the insertion point in the sections list (0..n).
  | { kind: "between-sections"; index: number };

export const MAX_ROW_CELLS = GRID_COLS / MIN_CELL_W;

export const rowAcceptsMerge = (
  row: EditableCell[],
  draggedFieldId: string,
  heightOf: (fieldId: string) => number
): boolean =>
  heightOf(draggedFieldId) === 1 &&
  row.every((c) => heightOf(c.field_id) === 1) &&
  row.filter((c) => c.field_id !== draggedFieldId).length < MAX_ROW_CELLS;

/** Re-place a field according to a drop decision, possibly across sections.
 * On merge, the dragged cell keeps its width as the weight when the row's
 * space is re-divided. */
export const applyFieldDrop = (
  layout: EditableLayout,
  sectionIndex: number,
  fieldId: string,
  target: FieldDropTarget,
  heightOf: (fieldId: string) => number
): EditableLayout => {
  const next = clone(layout);
  let dragged: EditableCell | null = null;
  let owner: EditableSection | null = null;
  for (const section of next.sections) {
    const cellIndex = section.cells.findIndex((c) => c.field_id === fieldId);
    if (cellIndex !== -1) {
      [dragged] = section.cells.splice(cellIndex, 1);
      owner = section;
      break;
    }
  }
  if (!dragged) return layout;
  const draggedCell = dragged;
  const finish = (): EditableLayout => {
    if (owner) owner.cells = normalizeFullRows(owner.cells, heightOf);
    // Untitled sections drained by the move disappear; empty groups stay.
    next.sections = next.sections.filter(
      (s) => s.title !== null || s.cells.length > 0
    );
    return next;
  };

  if (target.kind === "between-sections") {
    const index = Math.max(0, Math.min(target.index, next.sections.length));
    const before = next.sections[index - 1];
    const after = next.sections[index];
    if (before && before.title === null) {
      before.cells = normalizeFullRows(
        [...before.cells, { ...draggedCell, x: 0, y: 100_000, w: GRID_COLS }],
        heightOf
      );
    } else if (after && after.title === null) {
      after.cells = normalizeFullRows(
        [{ ...draggedCell, x: 0, y: -1, w: GRID_COLS }, ...after.cells],
        heightOf
      );
    } else {
      next.sections.splice(index, 0, {
        title: null,
        cells: [{ ...draggedCell, x: 0, y: 0, w: GRID_COLS }],
      });
    }
    return finish();
  }

  const section = next.sections[sectionIndex];
  if (!section) return layout;
  const rows = groupRows(section.cells);

  const insertOwnRow = (rowIndex: number) =>
    rows.splice(rowIndex, 0, [{ ...draggedCell, w: GRID_COLS }]);

  if (target.kind === "merge") {
    const rowIndex = rows.findIndex((r) => r[0].y === target.rowY);
    const row = rows[rowIndex];
    if (!row) {
      rows.push([{ ...draggedCell, w: GRID_COLS }]);
    } else if (rowAcceptsMerge(row, fieldId, heightOf)) {
      const index = Math.max(0, Math.min(target.index, row.length));
      row.splice(index, 0, draggedCell);
    } else {
      insertOwnRow(rowIndex + 1);
    }
  } else if (target.kind === "before-row") {
    const rowIndex = rows.findIndex((r) => r[0].y === target.rowY);
    insertOwnRow(rowIndex === -1 ? rows.length : rowIndex);
  } else {
    rows.push([{ ...draggedCell, w: GRID_COLS }]);
  }

  section.cells = flattenRows(rows, heightOf);
  return finish();
};

export const normalizeSectionRows = (
  layout: EditableLayout,
  sectionIndex: number,
  heightOf: (fieldId: string) => number
): EditableLayout => {
  const next = clone(layout);
  const section = next.sections[sectionIndex];
  if (!section) return layout;
  section.cells = normalizeFullRows(section.cells, heightOf);
  return next;
};

/** Per-type minimum width when resizing: compact widgets (boolean toggle)
 * can squeeze below the general minimum. */
export const minWidthLookup = (
  fields: InboxFieldResponse[]
): ((fieldId: string) => number) => {
  const byId = new Map(
    fields.map((f) => [f.id, f.type === "boolean" ? 2 : MIN_CELL_W])
  );
  return (fieldId) => byId.get(fieldId) ?? MIN_CELL_W;
};

/** Clamp a divider position between a cell and its right neighbor. */
export const clampPairSplit = (
  leftW: number,
  pairTotal: number,
  leftMin: number = MIN_CELL_W,
  rightMin: number = MIN_CELL_W
): number => Math.min(Math.max(leftW, leftMin), pairTotal - rightMin);

/** Move the divider between `leftFieldId` and its right-hand neighbor:
 * only that pair trades space, so the row total is preserved. */
export const resizeRowPair = (
  layout: EditableLayout,
  sectionIndex: number,
  leftFieldId: string,
  newLeftW: number,
  minOf: (fieldId: string) => number = () => MIN_CELL_W
): EditableLayout => {
  const next = clone(layout);
  const section = next.sections[sectionIndex];
  if (!section) return layout;
  const left = section.cells.find((c) => c.field_id === leftFieldId);
  if (!left) return layout;
  const row = readingOrder(section.cells.filter((c) => c.y === left.y));
  const leftIndex = row.findIndex((c) => c.field_id === leftFieldId);
  const right = row[leftIndex + 1];
  if (!right) return layout;
  const pairTotal = left.w + right.w;
  left.w = clampPairSplit(
    newLeftW,
    pairTotal,
    minOf(left.field_id),
    minOf(right.field_id)
  );
  right.w = pairTotal - left.w;
  let x = 0;
  for (const cell of row) {
    cell.x = x;
    x += cell.w;
  }
  return next;
};

/** Relocate fields whose group changed outside the designer (the property
 * dialog) into the section matching their new group, creating it when
 * missing. Untouched fields keep their in-progress arrangement. */
export const syncFieldGroups = (
  layout: EditableLayout,
  fields: InboxFieldResponse[],
  changedFieldIds: string[],
  heightOf: (fieldId: string) => number
): EditableLayout => {
  let next = layout;
  const byId = new Map(fields.map((f) => [f.id, f]));
  for (const fieldId of changedFieldIds) {
    const field = byId.get(fieldId);
    if (!field) continue;
    const title = field.group ?? null;
    const owner = next.sections.find((s) =>
      s.cells.some((c) => c.field_id === fieldId)
    );
    if (!owner || (owner.title ?? null) === title) continue;
    let targetIndex = next.sections.findIndex(
      (s) => (s.title ?? null) === title
    );
    if (targetIndex === -1) {
      next = {
        version: 1,
        sections: [...next.sections, { title, cells: [] }],
      };
      targetIndex = next.sections.length - 1;
    }
    next = moveFieldToSection(next, fieldId, targetIndex, heightOf);
  }
  next.sections.forEach((_, index) => {
    next = normalizeSectionRows(next, index, heightOf);
  });
  return next;
};

/** Swap a section with its neighbor (direction -1 = up, 1 = down). */
export const moveSection = (
  layout: EditableLayout,
  index: number,
  direction: -1 | 1
): EditableLayout => {
  const other = index + direction;
  if (!layout.sections[index] || !layout.sections[other]) return layout;
  const next = clone(layout);
  [next.sections[index], next.sections[other]] = [
    next.sections[other],
    next.sections[index],
  ];
  return next;
};

export const sectionTitleExists = (
  layout: EditableLayout,
  title: string | null,
  excludeIndex?: number
): boolean =>
  layout.sections.some(
    (s, i) => i !== excludeIndex && (s.title ?? null) === (title ?? null)
  );

export const addSection = (
  layout: EditableLayout,
  title: string
): EditableLayout => {
  if (sectionTitleExists(layout, title)) return layout;
  const next = clone(layout);
  next.sections.push({ title, cells: [] });
  return next;
};

export const renameSection = (
  layout: EditableLayout,
  index: number,
  title: string
): EditableLayout => {
  if (!layout.sections[index] || sectionTitleExists(layout, title, index)) {
    return layout;
  }
  const next = clone(layout);
  next.sections[index].title = title;
  return next;
};

/** Remove a section, relocating its cells below the untitled section's
 * content (creating that section at the removed position when missing). */
export const removeSection = (
  layout: EditableLayout,
  index: number,
  heightOf: (fieldId: string) => number
): EditableLayout => {
  const section = layout.sections[index];
  if (!section) return layout;
  const next = clone(layout);
  const [removed] = next.sections.splice(index, 1);
  if (removed.cells.length > 0) {
    let untitled = next.sections.find((s) => s.title === null);
    if (!untitled) {
      untitled = { title: null, cells: [] };
      next.sections.splice(index, 0, untitled);
    }
    const offset = sectionBottom(untitled, heightOf);
    for (const cell of readingOrder(removed.cells)) {
      untitled.cells.push({ ...cell, y: cell.y + offset });
    }
  }
  return next;
};
